import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { client } from '../../../api/NestClient';
import type { paths } from '../../../api/types/api';
import { RTK_CACHE_LIFETIME } from '../../../config';
import { nestBaseQuery } from '../../../utils/nestBaseQuery';

export type Gstr3bSummaryResponse =
  paths['/api/v1/gstr3b/summary']['get']['responses']['200']['content']['application/json'];

export type Gstr3bDateRangeQueryArgs = {
  fromDate: number;
  toDate: number;
};

type NestClientGetResult = Awaited<ReturnType<typeof client.GET>>;

const gstr3bNestBaseQuery: BaseQueryFn<
  NestClientGetResult | Promise<NestClientGetResult>,
  unknown,
  { message: string; status: number; originalResponse: unknown }
> = async (clientCall) => nestBaseQuery(await clientCall);

export const gstr3bApi = createApi({
  reducerPath: 'gstr3bApi',
  baseQuery: gstr3bNestBaseQuery,
  tagTypes: ['Gstr3bSummary'],
  endpoints: (builder) => ({
    getGstr3bSummary: builder.query<Gstr3bSummaryResponse | null, Gstr3bDateRangeQueryArgs>({
      query: ({ fromDate, toDate }) =>
        client.GET('/api/v1/gstr3b/summary', {
          params: { query: { fromDate, toDate } },
        }),
      transformResponse: (response: Gstr3bSummaryResponse | undefined) => response ?? null,
      providesTags: (_result, _error, { fromDate, toDate }) => [
        { type: 'Gstr3bSummary', id: `${fromDate}-${toDate}` },
      ],
      keepUnusedDataFor: RTK_CACHE_LIFETIME,
    }),
  }),
});

export const { useGetGstr3bSummaryQuery, useLazyGetGstr3bSummaryQuery } = gstr3bApi;

/** Call after sales/POS/purchase/credit/debit vouchers change so GSTR 3B summary refetches. */
export const invalidateGstr3bSummaryCache = (dispatch: (action: unknown) => unknown) => {
  dispatch(gstr3bApi.util.invalidateTags(['Gstr3bSummary']));
};
